import React from "react";
import { Link } from "react-router-dom";
import { Banknote, Car, Heart, ListChecks, LogOut, Sparkles, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const Account = () => {
  const { user, logout } = useAuth();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="account-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Your account</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
            Hi, {user?.full_name?.split(" ")[0] || "there"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{user?.email}</p>
        </div>
        <Button variant="outline" onClick={logout} data-testid="account-logout-btn">
          <LogOut size={16} /> Log out
        </Button>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-6">
        <Link
          to="/account/saved"
          className="group bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:border-brand transition-all"
          data-testid="account-saved-tile"
        >
          <div className="w-10 h-10 rounded-lg bg-brand-light text-brand flex items-center justify-center">
            <Heart size={18} />
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">Saved vehicles</h3>
          <p className="mt-1 text-sm text-slate-500">Track the cars you're interested in.</p>
          <p className="mt-4 text-xs font-semibold text-brand group-hover:underline">
            View saved →
          </p>
        </Link>

        <Link
          to="/account/leads"
          className="group bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:border-brand transition-all"
          data-testid="account-leads-tile"
        >
          <div className="w-10 h-10 rounded-lg bg-brand-light text-brand flex items-center justify-center">
            <ListChecks size={18} />
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">My inquiries</h3>
          <p className="mt-1 text-sm text-slate-500">
            See appointment requests you've submitted and their status.
          </p>
          <p className="mt-4 text-xs font-semibold text-brand group-hover:underline">
            View inquiries →
          </p>
        </Link>

        <Link
          to="/account/finance-quotes"
          className="group bg-white border-2 border-emerald-500/40 rounded-2xl p-6 hover:-translate-y-1 hover:border-emerald-600 transition-all"
          data-testid="account-finance-tile"
        >
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Banknote size={18} />
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">Financing quotes</h3>
          <p className="mt-1 text-sm text-slate-500">Saved monthly-payment scenarios per vehicle.</p>
          <p className="mt-4 text-xs font-semibold text-emerald-700 group-hover:underline">
            View quotes →
          </p>
        </Link>

        <Link
          to="/account/trade-ins"
          className="group bg-white border-2 border-gold/50 rounded-2xl p-6 hover:-translate-y-1 hover:border-gold transition-all"
          data-testid="account-tradein-tile"
        >
          <div className="w-10 h-10 rounded-lg bg-gold/20 text-[#7A5C0E] flex items-center justify-center">
            <Car size={18} />
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">Trade-in estimates</h3>
          <p className="mt-1 text-sm text-slate-500">Your saved trade-in values with dealer submission status.</p>
          <p className="mt-4 text-xs font-semibold text-[#7A5C0E] group-hover:underline">
            View estimates →
          </p>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="w-10 h-10 rounded-lg bg-gold-light text-gold flex items-center justify-center">
            <User size={18} />
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">Profile</h3>
          <ul className="mt-3 text-sm text-slate-600 space-y-1">
            <li><span className="text-slate-500 text-xs">Name:</span> {user?.full_name}</li>
            <li><span className="text-slate-500 text-xs">Email:</span> {user?.email}</li>
            {user?.phone && <li><span className="text-slate-500 text-xs">Phone:</span> {user.phone}</li>}
            <li>
              <span className="text-slate-500 text-xs">Sign-in:</span>{" "}
              {user?.auth_provider === "google" ? "Google" : "Email"}
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 border border-slate-200 rounded-2xl p-8 bg-gradient-to-br from-brand-light to-white">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-white text-brand border border-slate-200 flex items-center justify-center">
            <Sparkles size={18} />
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold text-slate-900">
              Not sure where to start?
            </h3>
            <p className="mt-1 text-sm text-slate-600 max-w-lg">
              Browse curated inventory across our five partner dealerships and save your favorites
              — our BDC team can put together a shortlist for you.
            </p>
            <Button asChild className="mt-4 bg-brand hover:bg-brand-hover text-white">
              <Link to="/inventory">Browse inventory</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
